/* ==========================================================================
   World of Mx. — "Mx. Matter" engine  (assets/mxw-world.js)
   Performance-first rebuild:
   - ONE requestAnimationFrame loop for the whole page.
   - Batched canvas rendering: fillStyle is set a fixed number of times per
     frame (not once per particle), and coordinates live in typed arrays.
     This is the fix "at source" for the main-thread starvation that froze
     the DOM reveals — the canvas is now cheap, so CSS reveals run smoothly.
   - Adaptive particle count + capped DPR by device capability.
   - Formations are cached (incl. glyph sampling); rebuilt only on major resize.
   - rAF pauses when the section is offscreen or motion is reduced.
   - Participation: pointer/touch feeds a live "trace" that seeds the final MX.
   - Namespaced, editor-safe, self-cleaning.
   ========================================================================== */
(() => {
  const roots = document.querySelectorAll('[data-mxw-root]');
  roots.forEach(init);
  document.addEventListener('shopify:section:load', (e) => {
    const r = e.target.querySelector ? e.target.querySelector('[data-mxw-root]') : null;
    if (r) init(r);
  });

  function init(root) {
    if (!root || root.dataset.mxwReady === '1') return;
    root.dataset.mxwReady = '1';

    let cfg = {};
    try { cfg = JSON.parse(root.querySelector('[data-mxw-config]').textContent); } catch (e) {}

    const prefersReduced = matchMedia('(prefers-reduced-motion: reduce)');
    const isTouch = matchMedia('(hover: none)').matches;
    const canvas = root.querySelector('[data-mxw-canvas]');
    const ctx = canvas && canvas.getContext ? canvas.getContext('2d', { alpha: true }) : null;
    const acts = [...root.querySelectorAll('[data-mxw-act]')];
    const progressEl = root.querySelector('[data-mxw-progress]');
    const dockEl = root.querySelector('[data-mxw-dock]');
    const motionBtn = root.querySelector('[data-mxw-motion]');
    const labelWrap = root.querySelector('[data-mxw-labels]');
    const labels = labelWrap ? [...labelWrap.querySelectorAll('[data-mxw-label]')] : [];
    const garment = root.querySelector('[data-mxw-garment]');

    /* ---------- device tier ---------- */
    const mem = navigator.deviceMemory || 4;
    const cores = navigator.hardwareConcurrency || 4;
    const small = innerWidth < 750;
    const low = small || mem <= 4 || cores <= 4;
    const N = cfg.quality === 'low' ? (small ? 700 : 1200)
            : cfg.quality === 'high' ? (small ? 1400 : 3200)
            : (small ? 900 : (low ? 1800 : 2600));
    const DPR = Math.min(devicePixelRatio || 1, low ? 1.5 : 2);

    /* ---------- colours ---------- */
    const ink = hex(cfg.ink || '#f4f1e8');
    const acc = hex(cfg.accent || '#ffd23e');
    const inkStyle = 'rgba(' + ink[0] + ',' + ink[1] + ',' + ink[2] + ',';
    // 7 spectrum buckets, cached as strings (used only in the light act)
    const spectrum = [];
    for (let i = 0; i < 7; i++) { const c = hsl(i / 7 * 320); spectrum.push('rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ','); }

    /* ---------- state ---------- */
    const px = new Float32Array(N), py = new Float32Array(N);   // current
    let A = null, B = null;                                     // active formation pair (Float32 x,y interleaved)
    let formations = [];                                        // per-act target arrays
    let t = 0, tTarget = 0;                                     // scene scalar
    let w = 0, h = 0, cx = 0, cy = 0, scale = 1;
    let raf = 0, running = false, motionOff = false, visible = true;
    const ptr = { x: -2, y: -2, on: false, nx: 0, ny: 0, press: 0 };
    // participation trace (normalised marks the visitor leaves)
    const TR = 40; const trace = new Float32Array(TR * 2); let traceN = 0, traceHead = 0;
    let boxBreak = 0;   // how much the visitor has broken the box (0..1)

    for (let i = 0; i < N; i++) { px[i] = rnd(-0.5, 0.5); py[i] = rnd(-0.5, 0.5); }

    /* ---------- formation generators (return Float32 interleaved x,y) ---------- */
    function scatter(s) { const a = new Float32Array(N * 2); for (let i = 0; i < N; i++) { a[i*2] = rnd(-s, s); a[i*2+1] = rnd(-s, s); } return a; }
    function point() { const a = new Float32Array(N * 2); for (let i = 0; i < N; i++) { const r = Math.pow(Math.random(), 7) * .03, th = Math.random() * 6.283; a[i*2] = Math.cos(th)*r; a[i*2+1] = Math.sin(th)*r; } return a; }
    function ring() { const a = new Float32Array(N * 2); for (let i = 0; i < N; i++) { const th = Math.random()*6.283, r = Math.sqrt(Math.random())*.44; a[i*2]=Math.cos(th)*r; a[i*2+1]=Math.sin(th)*r; } return a; }
    function box() { const a = new Float32Array(N * 2), W=.36, H=.44; for (let i = 0; i < N; i++) { const s=Math.random()*4|0, u=Math.random(); let x,y; if(s===0){x=-W+u*2*W;y=-H;}else if(s===1){x=W;y=-H+u*2*H;}else if(s===2){x=-W+u*2*W;y=H;}else{x=-W;y=-H+u*2*H;} a[i*2]=x+rnd(-.005,.005); a[i*2+1]=y+rnd(-.005,.005);} return a; }
    function bodyForm() { const a = new Float32Array(N * 2); for (let i = 0; i < N; i++) { const u=Math.random(); if(u<.16){const th=Math.random()*6.283,r=Math.sqrt(Math.random())*.075; a[i*2]=Math.cos(th)*r; a[i*2+1]=-.36+Math.sin(th)*r;} else {const yy=rnd(-.26,.48), tp=.22*(1-Math.abs(yy-.05)/.72); a[i*2]=rnd(-tp,tp); a[i*2+1]=yy;} } return a; }
    function tee() { const a = new Float32Array(N * 2); let n=0; while(n<N){ const u=Math.random(); let x,y; if(u<.22){const l=Math.random()<.5; y=rnd(-.26,-.05); x=(l?-1:1)*rnd(.25,.42);} else {y=rnd(-.3,.36); x=rnd(-.25,.25); if(y<-.22 && Math.abs(x)<.06 && Math.random()<.85) continue;} a[n*2]=x; a[n*2+1]=y; n++; } return a; }
    function wave() { const a = new Float32Array(N * 2); for (let i = 0; i < N; i++) { const rg=Math.random(), th=Math.random()*6.283, r=.04+rg*.48; a[i*2]=Math.cos(th)*r; a[i*2+1]=Math.sin(th)*r*.55; } return a; }
    function crowd() { const a = new Float32Array(N * 2), K=16, cs=[]; for(let c=0;c<K;c++) cs.push([rnd(-.46,.46), rnd(-.34,.34)]); for (let i = 0; i < N; i++) { const c=cs[i%K]; a[i*2]=c[0]+rnd(-.045,.045); a[i*2+1]=c[1]+rnd(-.045,.045);} return a; }
    function glyph(text) {
      const off = document.createElement('canvas'), W = 640, H = 320; off.width = W; off.height = H;
      const c = off.getContext('2d'); c.fillStyle = '#fff'; c.textAlign = 'center'; c.textBaseline = 'middle';
      let fs = 260; c.font = '800 ' + fs + 'px Arial, sans-serif';
      while (c.measureText(text).width > W * .9 && fs > 24) { fs -= 8; c.font = '800 ' + fs + 'px Arial, sans-serif'; }
      c.fillText(text, W/2, H/2);
      const d = c.getImageData(0, 0, W, H).data, pts = [];
      for (let y = 0; y < H; y += 3) for (let x = 0; x < W; x += 3) if (d[(y*W+x)*4+3] > 128) pts.push((x/W-.5)*.92, (y/H-.5)*.46);
      const a = new Float32Array(N * 2); if (!pts.length) return ring();
      for (let i = 0; i < N; i++) { const k = (i % (pts.length/2))*2; a[i*2]=pts[k]; a[i*2+1]=pts[k+1]; } return a;
    }
    const GEN = { scan: () => scatter(.5), point, field: ring, box, body: bodyForm,
      designer: () => scatter(.34), garment: tee, light: tee, signal: wave, crowd,
      haus: () => glyph(cfg.monogram || 'MX.'), wordmark: () => glyph(cfg.wordmark || 'MX.') };

    function buildFormations() { formations = acts.map(a => (GEN[a.dataset.formation] || GEN.field)()); }

    /* ---------- sizing ---------- */
    function resize() {
      if (!canvas) return;
      const r = canvas.getBoundingClientRect();
      w = Math.max(1, r.width); h = Math.max(1, r.height);
      canvas.width = Math.round(w * DPR); canvas.height = Math.round(h * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cx = w / 2; cy = h / 2; scale = Math.min(w, h) * .92;
    }

    /* ---------- scroll -> scene ---------- */
    function onScroll() {
      const r = root.getBoundingClientRect();
      const total = root.offsetHeight - innerHeight;
      const p = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
      tTarget = p * (acts.length - 1);
      if (progressEl) progressEl.style.transform = 'scaleX(' + p.toFixed(3) + ')';
      if (dockEl) dockEl.classList.toggle('is-on', tTarget >= 4.3 && p < .985);
    }

    /* ---------- pointer (participation) ---------- */
    function pointer(e) {
      const pt = e.touches ? e.touches[0] : e;
      const r = canvas.getBoundingClientRect();
      ptr.x = pt.clientX - r.left; ptr.y = pt.clientY - r.top; ptr.on = true;
      ptr.nx = (ptr.x - cx) / scale; ptr.ny = (ptr.y - cy) / scale;
      // record a trace mark (throttled by distance)
      const li = ((traceHead - 1 + TR) % TR) * 2;
      if (traceN === 0 || Math.hypot(ptr.nx - trace[li], ptr.ny - trace[li+1]) > .03) {
        trace[traceHead*2] = ptr.nx; trace[traceHead*2+1] = ptr.ny;
        traceHead = (traceHead + 1) % TR; traceN = Math.min(traceN + 1, TR);
      }
      const ai = Math.round(t);
      if (acts[ai]) { const f = acts[ai].dataset.formation; if (f === 'box') boxBreak = Math.min(1, boxBreak + .012); }
    }
    function pointerOut() { ptr.on = false; }

    /* ---------- the single draw ---------- */
    function draw() {
      ctx.clearRect(0, 0, w, h);
      const a = clamp(Math.floor(t), 0, acts.length - 1);
      const b = Math.min(acts.length - 1, a + 1);
      const f = t - a;
      A = formations[a]; B = formations[b]; if (!A) return;
      const actA = acts[a], actB = acts[b];
      const isLight = (actA && actA.dataset.formation === 'light') || (actB && actB.dataset.formation === 'light' && f > .35);
      const isHaus = (actA && (actA.dataset.formation === 'haus' || actA.dataset.formation === 'wordmark')) ||
                     (actB && (actB.dataset.formation === 'haus' || actB.dataset.formation === 'wordmark') && f > .4);
      const brk = ((actA && actA.dataset.formation === 'box') || (actB && actB.dataset.formation === 'box')) ? boxBreak : 0;
      const sz = low ? 1.5 : 1.8;

      // integrate positions toward interpolated target
      for (let i = 0; i < N; i++) {
        let tx = A[i*2] + (B[i*2] - A[i*2]) * f;
        let ty = A[i*2+1] + (B[i*2+1] - A[i*2+1]) * f;
        if (brk > 0) { // the visitor's pressure scatters the box outward
          const k = brk * (0.4 + (i % 7) * 0.09);
          tx += (px[i] - tx) * k + (Math.sin(i * 12.9) * .12 * brk);
          ty += (py[i] - ty) * k + (Math.cos(i * 4.1) * .12 * brk);
        }
        if (isHaus && traceN > 4 && (i % 9 === 0)) { // final MX carries the visitor's marks
          const ti = (i % traceN) * 2; tx = trace[ti] * .6 + tx * .4; ty = trace[ti+1] * .6 + ty * .4;
        }
        px[i] += (tx - px[i]) * .12; py[i] += (ty - py[i]) * .12;
      }

      // BATCHED render — the performance fix
      if (isLight) {
        for (let bkt = 0; bkt < 7; bkt++) {
          ctx.fillStyle = spectrum[bkt] + '.85)';
          for (let i = bkt; i < N; i += 7) ctx.fillRect(cx + px[i]*scale, cy + py[i]*scale, sz, sz);
        }
      } else {
        ctx.fillStyle = inkStyle + '.62)';
        for (let i = 0; i < N; i++) ctx.fillRect(cx + px[i]*scale, cy + py[i]*scale, sz, sz);
      }
      // pointer light — ONE radial gradient, not per-particle colour
      if (ptr.on && !isHaus) {
        const rad = 90 + ptr.press * 90;
        const g = ctx.createRadialGradient(ptr.x, ptr.y, 0, ptr.x, ptr.y, rad);
        const col = isLight ? '255,210,62' : (acc[0] + ',' + acc[1] + ',' + acc[2]);
        g.addColorStop(0, 'rgba(' + col + ',' + (isLight ? .5 : .32) + ')'); g.addColorStop(1, 'rgba(' + col + ',0)');
        ctx.fillStyle = g; ctx.fillRect(ptr.x - rad, ptr.y - rad, rad*2, rad*2);
      }
    }

    /* ---------- labels that chase, then fail (the scan act) ---------- */
    function labelsTick() {
      if (!labels.length) return;
      const ai = Math.round(t);
      const active = acts[ai] && acts[ai].dataset.formation === 'scan' && ptr.on;
      labelWrap.classList.toggle('is-live', !!active);
      if (!active) return;
      labels.forEach((el, i) => {
        if (el.dataset.failed) return;
        const cxl = el._x || (el._x = rnd(15, 85)), cyl = el._y || (el._y = rnd(20, 80));
        const px2 = (ptr.x / w) * 100, py2 = (ptr.y / h) * 100;
        const nx = cxl + (px2 - cxl) * .04, ny = cyl + (py2 - cyl) * .04;
        el._x = nx; el._y = ny;
        el.style.transform = 'translate(-50%,-50%) translate(' + nx + 'vw,' + ny + 'vh)';
        if (Math.hypot(px2 - nx, py2 - ny) < 6) { el.dataset.failed = '1'; el.classList.add('is-failed'); }
      });
      if (labels.every(l => l.dataset.failed)) labelWrap.classList.add('is-done');
    }

    /* ---------- hold-to-light on the real garment image ---------- */
    if (garment) {
      const gimg = garment;
      const move = (e) => { const pt = e.touches ? e.touches[0] : e; const r = gimg.getBoundingClientRect();
        gimg.style.setProperty('--lx', ((pt.clientX - r.left) / r.width * 100) + '%');
        gimg.style.setProperty('--ly', ((pt.clientY - r.top) / r.height * 100) + '%');
        gimg.classList.add('is-lit'); };
      const down = () => gimg.classList.add('is-press');
      const up = () => gimg.classList.remove('is-press');
      const leave = () => gimg.classList.remove('is-lit', 'is-press');
      gimg.addEventListener('pointermove', move); gimg.addEventListener('pointerdown', down);
      gimg.addEventListener('pointerup', up); gimg.addEventListener('pointerleave', leave);
      gimg.addEventListener('touchmove', (e)=>{ move(e); }, { passive: true });
      gimg._cleanup = () => { gimg.removeEventListener('pointermove', move); gimg.removeEventListener('pointerdown', down); gimg.removeEventListener('pointerup', up); gimg.removeEventListener('pointerleave', leave); };
    }

    /* ---------- loop ---------- */
    function frame() { t += (tTarget - t) * .07; if (ptr.press < 1 && root.dataset.press === '1') ptr.press = Math.min(1, ptr.press + .05); if (root.dataset.press !== '1') ptr.press = Math.max(0, ptr.press - .05); draw(); labelsTick(); raf = requestAnimationFrame(frame); }
    function start() { if (running || motionOff || !visible) return; running = true; raf = requestAnimationFrame(frame); }
    function stop() { running = false; cancelAnimationFrame(raf); }

    /* ---------- reveals (compositor-safe; cheap canvas keeps them smooth) ---------- */
    const io = new IntersectionObserver((es) => es.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-in'); }), { threshold: .2 });
    acts.forEach(a => io.observe(a));
    const vio = new IntersectionObserver((es) => es.forEach(e => { visible = e.isIntersecting; visible ? start() : stop(); }), { threshold: 0 });
    vio.observe(root);

    /* ---------- static / reduced ---------- */
    function setStatic(on) {
      motionOff = on; root.classList.toggle('is-static', on);
      if (motionBtn) motionBtn.setAttribute('aria-pressed', String(on));
      if (on) { stop(); if (ctx) ctx.clearRect(0, 0, w, h); acts.forEach(a => a.classList.add('is-in')); }
      else start();
    }

    /* ---------- bind ---------- */
    let rt; const onResize = () => { resize(); clearTimeout(rt); rt = setTimeout(() => { buildFormations(); }, 250); onScroll(); };
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onResize);
    root.addEventListener('pointermove', pointer, { passive: true });
    root.addEventListener('touchmove', pointer, { passive: true });
    root.addEventListener('pointerdown', () => root.dataset.press = '1');
    root.addEventListener('pointerup', () => root.dataset.press = '0');
    root.addEventListener('pointerleave', pointerOut);
    if (motionBtn) motionBtn.addEventListener('click', () => setStatic(!motionOff));
    if (prefersReduced.addEventListener) prefersReduced.addEventListener('change', e => setStatic(e.matches));

    /* ---------- boot ---------- */
    if (!ctx) { root.classList.add('is-static'); acts.forEach(a => a.classList.add('is-in')); return; }
    resize(); buildFormations(); onScroll();
    setStatic(cfg.motion === 'reduced' || prefersReduced.matches);

    /* ---------- editor cleanup ---------- */
    document.addEventListener('shopify:section:unload', (e) => {
      if (e.target === root || (e.target.contains && e.target.contains(root))) {
        stop(); io.disconnect(); vio.disconnect();
        removeEventListener('scroll', onScroll); removeEventListener('resize', onResize);
        if (garment && garment._cleanup) garment._cleanup();
        root.dataset.mxwReady = '';
      }
    });
  }

  /* ---------- helpers ---------- */
  function rnd(a, b) { return a + Math.random() * (b - a); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function hex(h) { const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((h || '#ffffff').trim()); return m ? [parseInt(m[1],16),parseInt(m[2],16),parseInt(m[3],16)] : [255,255,255]; }
  function hsl(deg) { const a = deg / 60, c = 1, x = 1 - Math.abs(a % 2 - 1); let r=0,g=0,b=0;
    if(a<1){r=c;g=x;}else if(a<2){r=x;g=c;}else if(a<3){g=c;b=x;}else if(a<4){g=x;b=c;}else if(a<5){r=x;b=c;}else{r=c;b=x;}
    return [Math.round(r*255),Math.round(g*255),Math.round(b*255)]; }
})();
